import { THEME_TOKENS, THEME_VARS } from "./themes";

// Live snapshot of the template actually applied to the document. Components
// (e.g. the National Day anthem) subscribe here instead of guessing from the
// DOM, so they stay in lock-step with applyTheme().
let currentTheme = { enabled: false, template: null };
const themeListeners = new Set();

function notifyTheme(enabled, template) {
  const next = {
    enabled: Boolean(enabled),
    template: enabled && template ? template : null,
  };
  currentTheme = next;
  themeListeners.forEach((fn) => fn(next));
}

export function getCurrentTheme() {
  return currentTheme;
}

export function subscribeTheme(fn) {
  themeListeners.add(fn);
  return () => themeListeners.delete(fn);
}

// Applies (or clears) a site template by writing the brand CSS variables onto
// the document root. Clearing falls back to the :root defaults in index.css,
// i.e. the original "classic" design with no changes.
export function applyTheme(slug, enabled) {
  const root = document.documentElement;

  // Always start from a clean slate so switching / disabling is instant.
  // Note: we use a dedicated `data-site-template` attribute because UI
  // libraries (e.g. Chakra) own `data-theme` and overwrite it at runtime.
  THEME_VARS.forEach((v) => root.style.removeProperty(v));
  root.removeAttribute("data-site-template");

  if (!enabled) {
    notifyTheme(false, null);
    return;
  }

  const tokens = THEME_TOKENS[slug];
  if (!tokens) {
    notifyTheme(false, null);
    return; // unknown slug -> keep default design
  }

  root.setAttribute("data-site-template", slug);
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  notifyTheme(true, slug);
}

// Returns the template slug forced through the URL (?preview_template=slug).
// Used by the admin live preview so a template can be inspected on the real
// site without saving it or affecting visitors.
export function getPreviewTemplate() {
  try {
    const slug = new URLSearchParams(window.location.search).get(
      "preview_template"
    );
    return slug && THEME_TOKENS[slug] ? slug : null;
  } catch (e) {
    return null;
  }
}

const STORAGE_KEY = "siteTheme";

// Applies the theme cached from a previous visit (if any) to avoid a flash of
// the wrong palette before the network request resolves.
export function applyCachedTheme() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const { enabled, template } = JSON.parse(raw);
    applyTheme(template, enabled);
  } catch (e) {
    // Ignore malformed cache.
  }
}

export function cacheTheme(enabled, template) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ enabled, template })
    );
  } catch (e) {
    // Ignore storage errors (e.g. private mode).
  }
}
